import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

/**
 * PTV2-1524: client for the per-provider OIDC login-claim capture. Unlike the rest of admin-auth this
 * talks to the GATEWAY-hosted control/read surface at `/gateway/auth-capture/...` (not the core
 * `/api/v1` API), because the capture data lives in the gateway's Redis. CSRF is disabled on the
 * gateway BFF chain, so a plain credentialed fetch suffices. Admin-only (the gateway enforces
 * PLATFORM_ADMIN from the session).
 */

const CAPTURE_BASE = '/gateway/auth-capture';

export interface CaptureStatus {
  readonly enabled: boolean;
  readonly secondsRemaining: number;
  readonly sessionCount: number;
}

export interface CaptureSummary {
  readonly id: string;
  readonly capturedAt: string;
  readonly subject: string | null;
  readonly outcome: string;
}

function encode(value: string): string {
  return encodeURIComponent(value);
}

async function captureFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${CAPTURE_BASE}${path}`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
    ...init,
  });
  if (!response.ok) {
    throw new Error(`capture request failed (${String(response.status)})`);
  }
  return (await response.json()) as T;
}

export const captureQueryKeys = {
  status: (host: string) => ['admin', 'auth-capture', host, 'status'] as const,
  sessions: (host: string) => ['admin', 'auth-capture', host, 'sessions'] as const,
};

/** Poll the capture status while the section is open (so the countdown + count stay live). */
export function useCaptureStatus(host: string, enabled: boolean): UseQueryResult<CaptureStatus> {
  return useQuery<CaptureStatus>({
    queryKey: captureQueryKeys.status(host),
    enabled: enabled && host.length > 0,
    refetchInterval: enabled ? 5000 : false,
    queryFn: () => captureFetch<CaptureStatus>(`/${encode(host)}/status`),
  });
}

export function useCaptureSessions(host: string, enabled: boolean): UseQueryResult<CaptureSummary[]> {
  return useQuery<CaptureSummary[]>({
    queryKey: captureQueryKeys.sessions(host),
    enabled: enabled && host.length > 0,
    refetchInterval: enabled ? 5000 : false,
    queryFn: () => captureFetch<CaptureSummary[]>(`/${encode(host)}/sessions`),
  });
}

/** Fetch one captured session's full (redacted) claim + userinfo document. */
export function fetchCaptureSession(host: string, id: string): Promise<Record<string, unknown>> {
  return captureFetch<Record<string, unknown>>(`/${encode(host)}/sessions/${encode(id)}`);
}

export function useCaptureControls(host: string): {
  readonly start: UseMutationResult<CaptureStatus, Error, number>;
  readonly stop: UseMutationResult<CaptureStatus, Error, void>;
  readonly clear: UseMutationResult<CaptureStatus, Error, void>;
} {
  const queryClient = useQueryClient();
  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey: captureQueryKeys.status(host) }).catch(() => undefined);
    queryClient.invalidateQueries({ queryKey: captureQueryKeys.sessions(host) }).catch(() => undefined);
  };

  const start = useMutation<CaptureStatus, Error, number>({
    mutationFn: (ttlSeconds) =>
      captureFetch<CaptureStatus>(`/${encode(host)}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ttlSeconds }),
      }),
    onSettled: invalidate,
  });
  const stop = useMutation<CaptureStatus>({
    mutationFn: () => captureFetch<CaptureStatus>(`/${encode(host)}/stop`, { method: 'POST' }),
    onSettled: invalidate,
  });
  const clear = useMutation<CaptureStatus>({
    mutationFn: () => captureFetch<CaptureStatus>(`/${encode(host)}/clear`, { method: 'POST' }),
    onSettled: invalidate,
  });

  return { start, stop, clear };
}
